import { supabase } from "@/lib/supabase";

type AdminMfaEnvelope<T> = {
  success: boolean;
  data?: T;
  error?: { code?: string; message?: string };
};

export type TrustedDeviceItem = {
  id: string;
  deviceLabel: string | null;
  createdAt: string | null;
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdUserAgent: string | null;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

async function invokeAdminMfaAction<TData>(
  action: string,
  payload: Record<string, unknown> = {},
): Promise<TData> {
  const { data, error } = await supabase.functions.invoke("admin-mfa-access", {
    body: { action, payload },
  });

  if (error) {
    throw new Error(error.message || "Falha ao chamar admin-mfa-access");
  }

  const envelope = (data ?? {}) as AdminMfaEnvelope<TData>;
  if (!envelope.success) {
    throw new Error(envelope.error?.message ?? "Ação MFA falhou");
  }
  if (envelope.data === undefined) {
    throw new Error("Resposta inválida da Edge Function");
  }

  return envelope.data;
}

function mapTrustedDevice(value: unknown): TrustedDeviceItem | null {
  const row = asRecord(value);
  if (!row) return null;
  const id = asString(row.id);
  if (!id) return null;
  return {
    id,
    deviceLabel: asString(row.device_label),
    createdAt: asString(row.created_at),
    lastUsedAt: asString(row.last_used_at),
    expiresAt: asString(row.expires_at),
    createdUserAgent: asString(row.created_user_agent),
  };
}

export async function createTrustedDevice(params: {
  token: string;
  fingerprint: string;
  label?: string;
  ttlDays?: number;
}) {
  const result = await invokeAdminMfaAction<{ trusted_device?: unknown }>(
    "create_trusted_device",
    {
      token: params.token,
      device_fingerprint: params.fingerprint,
      device_label: params.label ?? null,
      ttl_days: params.ttlDays ?? null,
    },
  );
  return {
    trustedDevice: mapTrustedDevice(result.trusted_device ?? null),
  };
}

export async function validateTrustedDevice(params: {
  token: string;
  fingerprint: string;
}) {
  const result = await invokeAdminMfaAction<{
    valid?: boolean;
    reason?: string | null;
    trusted_device_id?: string | null;
    expires_at?: string | null;
  }>("validate_trusted_device", {
    token: params.token,
    device_fingerprint: params.fingerprint,
  });

  return {
    valid: result.valid === true,
    reason: asString(result.reason) ?? null,
    trustedDeviceId: asString(result.trusted_device_id),
    expiresAt: asString(result.expires_at),
  };
}

export async function listMyTrustedDevices(): Promise<TrustedDeviceItem[]> {
  const result = await invokeAdminMfaAction<{ trusted_devices?: unknown[] }>(
    "list_my_trusted_devices",
  );
  const rows = Array.isArray(result.trusted_devices) ? result.trusted_devices : [];
  return rows.map(mapTrustedDevice).filter((item): item is TrustedDeviceItem => item !== null);
}

export async function revokeTrustedDevice(params: {
  deviceId?: string;
  revokeAll?: boolean;
}) {
  const result = await invokeAdminMfaAction<{ revoked_count?: number }>(
    "revoke_trusted_device",
    {
      device_id: params.deviceId ?? null,
      revoke_all: params.revokeAll === true,
    },
  );
  return { revokedCount: typeof result.revoked_count === "number" ? result.revoked_count : 0 };
}

export async function prepareTotpChallenge(params: { factorId: string }) {
  // Always create challenge directly from the browser.
  // Edge function creates challenges from server IP, causing IP mismatch
  // on verify (Supabase Auth returns 422 when challenge IP ≠ verify IP).
  const { data, error } = await supabase.auth.mfa.challenge({
    factorId: params.factorId,
  });

  if (error || !data?.id) {
    throw new Error(
      `Falha ao criar challenge TOTP: ${error?.message ?? "unknown_error"}`,
    );
  }

  return {
    challengeId: data.id,
    factorId: params.factorId,
    expiresAt: typeof data.expires_at === "number" ? data.expires_at : null,
  };
}

export async function resetAdminMfa(params: {
  targetUserId: string;
  reason: string;
}) {
  return invokeAdminMfaAction<{
    target_user_id?: string;
    deleted_factors_count?: number;
    revoked_trusted_devices_count?: number;
  }>("reset_admin_mfa", {
    target_user_id: params.targetUserId,
    reason: params.reason,
  });
}

export async function markMfaReenrolled() {
  return invokeAdminMfaAction<{
    user_id?: string;
    admin_mfa_reenroll_required?: boolean;
  }>("mark_mfa_reenrolled", {});
}
